import type { Appearance } from "@stripe/stripe-js";
import fontFamily from "@shared/theme/props/fontFamily";
import { getStripeColors } from "@shared/theme/theme";
import { isDarkMode } from "@shared/theme/utils/color-mode";

export type StripeColorMode = "light" | "dark";

/**
 * Resolves the current Stripe color mode from the DOM.
 * Convenience wrapper so callers don't need to import isDarkMode directly.
 */
export function resolveStripeColorMode(): StripeColorMode {
  return isDarkMode() ? "dark" : "light";
}

/**
 * Builds the Appearance object used by the shared Stripe `elementsInstance`.
 * Covers elements that support the Appearance API (wallet, PaymentElement, etc.).
 */
export function buildStripeAppearance(mode: StripeColorMode): Appearance {
  const colors = getStripeColors(mode);
  const isDark = mode === "dark";

  return {
    variables: {
      fontSizeBase: "16px",
      fontFamily: fontFamily.sans.join(","),
      colorText: isDark ? colors.fontLight : colors.fontDark,
      colorPrimary: colors.primary800,
      borderRadius: "4px",
      colorDanger: colors.red600,
    },
    rules: {
      ".Input": {
        border: "1px solid",
        borderColor: colors.gray400,
        padding: "12px",
      },
    },
  };
}

/**
 * Builds the legacy `style` object for individual card elements
 * (cardNumber, cardExpiry, cardCvc) that don't support the Appearance API.
 */
export function buildCardElementStyle(mode: StripeColorMode): {
  base: Record<string, unknown>;
  empty: Record<string, string>;
  invalid: Record<string, string>;
} {
  const colors = getStripeColors(mode);
  const isDark = mode === "dark";

  return {
    base: {
      fontSize: "16px",
      lineHeight: "40px",
      fontFamily: fontFamily.sans.join(","),
      color: isDark ? colors.fontLight : colors.fontDark,
      fontSmoothing: "antialiased",
      "::placeholder": {
        color: colors.gray500,
      },
      ":-webkit-autofill": {
        color: isDark ? colors.fontLight : colors.fontDark,
      },
    },
    empty: {
      color: colors.gray500,
    },
    invalid: {
      color: colors.red600,
    },
  };
}

/**
 * Builds the Appearance object for the paymentMethodMessaging element.
 * Messaging elements render directly on the page (no iframe background),
 * so they need their own appearance tuned for text colors.
 */
export function buildMessagingAppearance(mode: StripeColorMode): Appearance {
  const colors = getStripeColors(mode);
  const isDark = mode === "dark";

  return {
    theme: isDark ? "night" : "stripe",
    variables: {
      colorText: isDark ? colors.fontLight : colors.fontDark,
      colorTextSecondary: colors.gray600,
    },
  };
}

/**
 * Builds the style options for the legacy affirmMessage element.
 */
export function buildAffirmStyle(mode: StripeColorMode): {
  fontColor: string;
  logoColor: "white" | "primary";
} {
  const colors = getStripeColors(mode);
  const isDark = mode === "dark";

  return {
    fontColor: isDark ? colors.fontLight : colors.fontDark,
    logoColor: isDark ? "white" : "primary",
  };
}
